import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import {
  uploadInvoice,
  uploadInvoiceReset,
  fetchUploadedInvoices,
} from '../../actions/invoiceUpload.action';
import {
  PageWrapper,
  PageTitle,
  PageIntro,
  SectionTitle,
  UploadSection,
  DropZone,
  DropZoneLabel,
  FileList,
  FileChip,
  UploadButton,
  UploadingOverlay,
  SuccessMessage,
  ErrorMessage,
  InvoiceTable,
  StatusBadge,
  Pagination,
  EmptyState,
  LoadingWrapper,
  ViewLink,
} from './InvoiceUploadPage.style';
import Loading from '../../components/Loading/Loading';
import {GoogleAnalyticsHelper} from '../../helpers/googleAnalytics.helper';
import {
  ANALYTICS_EVENTS,
  INVOICE_UPLOAD_ACCEPTED_TYPES,
  INVOICE_UPLOAD_STATUS_LABELS,
} from '../../constants/constants';
import {
  formatDate,
  formatCurrency,
  validateFile,
} from '../../helpers/invoiceUpload.helper';

const InvoiceUploadPage = ({history}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const {auth, invoiceUpload} = useSelector(state => ({
    auth: state.auth,
    invoiceUpload: state.invoiceUpload || {},
  }));

  const uploading = invoiceUpload.uploading || false;
  const uploadError = invoiceUpload.uploadError || null;
  const uploadResult = invoiceUpload.uploadResult || null;
  const invoices = invoiceUpload.invoices || [];
  const invoicesLoading = invoiceUpload.invoicesLoading || false;
  const invoicesError = invoiceUpload.invoicesError || null;
  const page = invoiceUpload.page || {page: 0, totalPages: 0, totalElements: 0};

  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileErrors, setFileErrors] = useState([]);

  useEffect(() => {
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.COMPARE_PRICES_PAGE_VIEW,
      {category: 'Compare Prices'}
    );
  }, []);

  useEffect(() => {
    if (auth) {
      dispatch(fetchUploadedInvoices(0));
    }
  }, [auth]);

  useEffect(() => {
    if (uploadResult) {
      setFiles([]);
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.COMPARE_PRICES_UPLOAD_SUCCESS,
        {category: 'Compare Prices'}
      );
    }
  }, [uploadResult]);

  useEffect(() => {
    if (uploadError) {
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.COMPARE_PRICES_UPLOAD_FAILED,
        {category: 'Compare Prices', error: uploadError}
      );
    }
  }, [uploadError]);

  const handleFiles = useCallback(newFiles => {
    const errors = [];
    const valid = [];
    Array.from(newFiles).forEach(f => {
      const err = validateFile(f);
      if (err) {
        errors.push(err);
      } else {
        valid.push(f);
      }
    });
    setFileErrors(errors);
    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
      dispatch(uploadInvoiceReset());
    }
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleFileInput = useCallback(
    e => {
      handleFiles(e.target.files);
      e.target.value = '';
    },
    [handleFiles]
  );

  const removeFile = useCallback(index => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpload = useCallback(() => {
    if (files.length > 0 && !uploading) {
      try {
        GoogleAnalyticsHelper.trackEvent(
          ANALYTICS_EVENTS.COMPARE_PRICES_UPLOAD,
          {
            category: 'Compare Prices',
            fileCount: files.length,
          }
        );
      } catch (e) {}

      dispatch(uploadInvoice(files));
    }
  }, [files, uploading]);

  const handleRowClick = useCallback(id => {
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.COMPARE_PRICES_VIEW_DETAIL,
      {category: 'Compare Prices', invoiceId: id}
    );
    history.push(`/invoice-upload/${id}`);
  }, []);

  const handlePageChange = useCallback(newPage => {
    dispatch(fetchUploadedInvoices(newPage));
  }, []);

  if (!auth) {
    return (
      <PageWrapper>
        <PageTitle>Compare Prices</PageTitle>
        <EmptyState>Please log in to upload invoices.</EmptyState>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageTitle>Compare Prices</PageTitle>

      <PageIntro>
        <p>
          Are you getting the best prices from your current suppliers? Upload an
          invoice from any supplier and we&apos;ll compare every item against
          our catalogue to show you exactly where you could save.
        </p>
        <p>
          Simply upload a PDF or photo of your invoice below. We&apos;ll analyse
          it, match the products, and{' '}
          <strong>email you a full savings report</strong> showing a
          side-by-side price comparison.
        </p>
      </PageIntro>

      <UploadSection>
        {uploading && (
          <UploadingOverlay>
            <Loading isLight={false} />
            <p>Uploading your invoice...</p>
            <span>This may take a moment for larger files</span>
          </UploadingOverlay>
        )}
        <SectionTitle>Upload an invoice</SectionTitle>
        <DropZone
          $isDragOver={isDragOver}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          <DropZoneLabel>
            Drop your invoice here or click to browse
          </DropZoneLabel>
          <p>Supported formats: PDF, JPEG, PNG, WebP</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={INVOICE_UPLOAD_ACCEPTED_TYPES.join(',')}
            onChange={handleFileInput}
            style={{display: 'none'}}
          />
        </DropZone>

        {files.length > 0 && (
          <FileList>
            {files.map((file, i) => (
              <FileChip key={`${file.name}-${i}`}>
                {file.name}
                <button onClick={() => removeFile(i)}>&times;</button>
              </FileChip>
            ))}
          </FileList>
        )}

        <UploadButton
          onClick={handleUpload}
          disabled={files.length === 0 || uploading}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </UploadButton>

        {fileErrors.length > 0 && (
          <ErrorMessage>
            {fileErrors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </ErrorMessage>
        )}

        {uploadResult && (
          <SuccessMessage>
            Invoice uploaded successfully! We&apos;re now analysing your invoice
            and matching the products. Once complete, we&apos;ll{' '}
            <strong>email you a detailed savings report</strong> and the results
            will also appear in the list below.
          </SuccessMessage>
        )}

        {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
      </UploadSection>

      <SectionTitle>Your uploaded invoices</SectionTitle>

      {invoicesLoading && (
        <LoadingWrapper>
          <Loading isLight={false} />
        </LoadingWrapper>
      )}

      {invoicesError && <ErrorMessage>{invoicesError}</ErrorMessage>}

      {!invoicesLoading && !invoicesError && invoices.length === 0 && (
        <EmptyState>
          No invoices uploaded yet. Upload your first invoice above to see how
          much you could save with JJ.
        </EmptyState>
      )}

      {!invoicesLoading && invoices.length > 0 && (
        <>
          <InvoiceTable>
            <thead>
              <tr>
                <th>Status</th>
                <th>Supplier</th>
                <th>Invoice No.</th>
                <th>Date</th>
                <th>Savings</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} onClick={() => handleRowClick(inv.id)}>
                  <td>
                    <StatusBadge $status={inv.status}>
                      {INVOICE_UPLOAD_STATUS_LABELS[inv.status] || inv.status}
                    </StatusBadge>
                  </td>
                  <td>{inv.supplierName || '-'}</td>
                  <td>{inv.invoiceNumber || '-'}</td>
                  <td>{formatDate(inv.invoiceDate || inv.uploadedAt)}</td>
                  <td>
                    {inv.totalSavings != null && inv.totalSavings > 0
                      ? formatCurrency(inv.totalSavings)
                      : inv.totalSavings != null && inv.totalSavings <= 0
                      ? 'See details'
                      : '-'}
                  </td>
                  <td>
                    <ViewLink>View &rsaquo;</ViewLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </InvoiceTable>

          {page.totalPages > 1 && (
            <Pagination>
              <button
                disabled={page.page === 0}
                onClick={() => handlePageChange(page.page - 1)}
              >
                Previous
              </button>
              <span>
                Page {page.page + 1} of {page.totalPages}
              </span>
              <button
                disabled={page.page >= page.totalPages - 1}
                onClick={() => handlePageChange(page.page + 1)}
              >
                Next
              </button>
            </Pagination>
          )}
        </>
      )}
    </PageWrapper>
  );
};

InvoiceUploadPage.propTypes = {
  history: PropTypes.object.isRequired,
};

export default InvoiceUploadPage;
